<template>
    <section>

        <ContentLayer class="chapter background" 
            v-if="chapter.background"
            :content="chapter" :level="'chapter'" :layer="'background'"
            :structure="structure" :navigationData="navigationData" :active="active" 
            :key="'slide-' + chapter.uri + '-background'" />
        
        <section class="chapter-slides fullscreen content-layer">

            <Slide class="slide slide-previous fullscreen"
                v-if="renderPrevious"
                :settings="settings"
                :navigationData="navigationData"
                :slideIndex="previousIndex"
                :chapterIndex="chapterIndex"
                :presentationIndex="presentationIndex"
                :structure="structure"
                :animating="slideAnimating"
                :chapter="chapter"
                :key="keyPrevious" />

            <Slide class="slide slide-current fullscreen"
                v-if="renderCurrent"
                :settings="settings"
                :navigationData="navigationData"
                :slideIndex="currentIndex"
                :chapterIndex="chapterIndex"
                :presentationIndex="presentationIndex"
                :structure="structure"
                :animating="slideAnimating"
                :chapter="chapter"
                :key="keyCurrent" />

            <Slide class="slide slide-next fullscreen"
                v-if="renderNext"
                :settings="settings"
                :navigationData="navigationData"
                :slideIndex="nextIndex"
                :chapterIndex="chapterIndex"
                :presentationIndex="presentationIndex"
                :structure="structure"
                :animating="slideAnimating"
                :chapter="chapter"
                :key="keyNext" />

        </section>
        
        <ContentLayer class="chapter foreground" 
            v-if="chapter.foreground"
            :content="chapter" :level="'chapter'" :layer="'foreground'"
            :structure="structure" :navigationData="navigationData" :active="active" 
            :key="'slide-' + chapter.uri + '-background'" />

    </section>
</template>

<script>
import Slide from '@/core/components/Slide'
import navigationMixin from '@/core/mixins/navigationMixin.js'
import { TweenLite, CSSPlugin, Power1, Power2, Power3 } from 'gsap'

const plugins = [CSSPlugin];

export default {
    data: () => {
        return {
            slideAnimating: false,
            previousIndex: false,
            currentIndex: false,
            nextIndex: false,
            animationForward: false,
            animationBackward: false
        }
    },
    watch: {
        active(val) {
            if (val) this.onActivated
        },
        currentSlideIndex(newVal, oldVal) {

            if (this.animationForward) {
                this.animationForward.progress(1).kill()
                this.animationForward = false
            }

            if (this.animationBackward) {
                this.animationBackward.progress(1).kill()
                this.animationBackward = false
            }

            if (oldVal === false) {
                this.onAnimationReady(newVal)
                return
            } else {
                this.onAnimationReady(oldVal)
            }
 
            if (newVal > oldVal) {
                this.nextIndex = newVal
                this.slideForward(newVal)
            }
            if (newVal < oldVal) {        
                this.previousIndex = newVal
                this.slideBackward(newVal)
            }
        }
    },
    mixins: [navigationMixin],
    computed: {
        active() {
            return this.presentationIndex === this.currentPresentationIndex 
                && this.chapterIndex === this.currentChapterIndex
        },
        renderPrevious() {
            if (this.previousIndex === false) return false
            if (!this.slides[this.previousIndex]) return false
            return true
        },
        renderCurrent() {
            if (this.currentIndex === false) return false
            if (!this.slides[this.currentIndex]) return false
            return true
        },
        renderNext() {
            if (this.nextIndex === false) return false
            if (!this.slides[this.nextIndex]) return false
            return true
        },
        keyPrevious() {
            if (!this.renderPrevious) return 'slide-previous-none' 
            return 'slide-' + this.currentChapter.uri + '-' + this.slides[this.previousIndex].uri
        },
        keyCurrent() {
            if (!this.renderCurrent) return 'slide-current-none' 
            return 'slide-' + this.currentChapter.uri + '-' + this.slides[this.currentIndex].uri
        },
        keyNext() {
            if (!this.renderNext) return 'slide-next-none'
            return 'slide-' + this.currentChapter.uri + '-' + this.slides[this.nextIndex].uri
        },
        slides() {
            return this.currentChapter.slides
        }
    },
    methods: {
        onActivated() {
            if (!this.settings.disableNavigationDebugLogging) console.log("chapter:", this.chapter.uri)
        },
        slideForward(index) {
            this.slideAnimating = true
            this.animationForward = TweenLite.fromTo(".chapter-slides", {x: "0%"}, {duration: this.settings.slideTransitionSpeed, ease: Power1.easeInOut, x: "-100%", onComplete: () => {
                this.onAnimationReady(index)
            }})
        },
        slideBackward(index) {
            this.slideAnimating = true
            this.animationBackward = TweenLite.fromTo(".chapter-slides", {x: "0%"}, {duration: this.settings.slideTransitionSpeed, ease: Power1.easeInOut, x: "100%", onComplete: () => {
                this.onAnimationReady(index)
            }})
        },
        initializeChapter() {
            this.onAnimationReady(0)

            this.$el.style.backgroundColor = this.settings.chapterBackgroundColor
        },
        onAnimationReady(index) {
            this.currentIndex = index
            TweenLite.set(".chapter-slides", {x: "0%"})

            if (this.currentIndex > 0) {
                this.previousIndex = this.currentIndex - 1
            } else {
                this.previousIndex = false
            }

            if (this.currentIndex < this.slides.length - 1) {
                this.nextIndex = this.currentIndex + 1
            } else {
                this.nextIndex = false
            }

            this.slideAnimating = false
        }
    },
    props: ['settings', 'presentationIndex', 'chapterIndex', 'chapter'],
    components: {
       Slide
    },
    created() {},
    mounted() {
        if (this.active) this.onActivated()
        this.initializeChapter()
    }
}
</script>

<style scoped lang="scss">

    @import '~@/core/assets/scss/_variables.scss';

    .chapter.background {
        z-index: 0;
    }

    .chapter-slides {
        z-index: 1;

        .slide {
            overflow: hidden;

            &.slide-previous {
                transform: translate(-100%, 0);
            }

            &.slide-next {
                transform: translate(100%, 0);
            }
        }
    }

    .chapter.foreground {
        z-index: 2;
        pointer-events: none;
    }

</style>
